/**
 * PostHog Event Tracking — Arspreet Global
 *
 * Events tracked:
 *  1. Product Viewed          — PDP page load
 *  2. Bundle Pack Selected    — Kaching bundle widget pack click
 *  3. Added to Cart           — ATC button submit (source: pdp_button | buy_now)
 *  4. Buy Now Clicked         — Accelerated checkout / payment button click
 *  5. Homepage Banner Clicked — AI slideshow banner click (handled in ai_gen_block_311d624.liquid)
 *  6. Homepage Product Card Clicked — Product card click on homepage
 *  7. BYOB Added to Box           — sections/byob.liquid (product added to bundle builder)
 *  8. BYOB Bundle Added to Cart   — sections/byob.liquid (bundle submitted to cart)
 */
(function () {
  'use strict';

  /**
   * Wait for PostHog to be ready before attaching listeners.
   * Retries up to ~10 seconds.
   */
  function waitForPosthog(cb) {
    var attempts = 0;
    function check() {
      if (window.posthog && typeof window.posthog.capture === 'function') {
        cb();
      } else if (attempts < 50) {
        attempts++;
        setTimeout(check, 200);
      }
    }
    check();
  }

  waitForPosthog(function () {

    // ─── 1. Product Viewed ────────────────────────────────────────────────────
    // Data injected by sections/product-information.liquid as window.__posthogProductData
    if (window.__posthogProductData) {
      posthog.capture('Product Viewed', window.__posthogProductData);
    }

    // ─── Variant change — keep __posthogProductData in sync ──────────────────
    // Listen for the theme's variant:update event so price & variant_title stay
    // current when the user switches variants.
    document.addEventListener('variant:update', function (event) {
      if (!window.__posthogProductData) return;
      var variant = event.detail && event.detail.resource;
      if (!variant) return;
      window.__posthogProductData.variant_id    = variant.id;
      window.__posthogProductData.variant_title = variant.title;
      window.__posthogProductData.price         = variant.price / 100;
    });

    // ─── 3. Added to Cart ─────────────────────────────────────────────────────
    // CartAddEvent fires as 'cart:update' (bubbles) from product-form-component.
    // We ignore error events and events not from the product form.
    document.addEventListener('cart:update', function (event) {
      var detail = event.detail;
      if (!detail) return;
      var data = detail.data;
      if (!data || data.source !== 'product-form-component' || data.didError) return;

      // Walk up to the product-form-component element to read current form state
      var productForm = event.target && event.target.closest
        ? event.target.closest('product-form-component')
        : null;

      var form        = productForm ? productForm.querySelector('form') : null;
      var variantId   = form ? (form.querySelector('input[name="id"]') || {}).value || '' : '';

      var pd          = window.__posthogProductData || {};
      var isBuyNow    = productForm && productForm._posthogBuyNow;

      var cartProps = {
        product_id:    pd.product_id    || data.productId || '',
        product_name:  pd.product_name  || '',
        variant_id:    variantId        || pd.variant_id  || '',
        variant_title: pd.variant_title || '',
        quantity:      data.itemCount   || 1,
        price:         pd.price         || 0,
        currency:      pd.currency      || 'INR',
        source:        isBuyNow ? 'buy_now' : 'pdp_button'
      };

      posthog.capture('Added to Cart', cartProps);

      // ─── 4. Buy Now Clicked ───────────────────────────────────────────────
      if (isBuyNow) {
        posthog.capture('Buy Now Clicked', {
          product_id:    cartProps.product_id,
          product_name:  cartProps.product_name,
          variant_id:    cartProps.variant_id,
          variant_title: cartProps.variant_title,
          quantity:      cartProps.quantity,
          price:         cartProps.price,
          currency:      cartProps.currency
        });
        delete productForm._posthogBuyNow;
      }
    });

    // Mark the parent product-form-component when a payment / Buy Now button is
    // clicked (capture phase so we run before Shopify's own handler).
    document.addEventListener('click', function (event) {
      var btn = event.target && event.target.closest
        ? event.target.closest(
            '.shopify-payment-button__button,' +
            '.accelerated-checkout-block button,' +
            '.accelerated-checkout-block [role="button"]'
          )
        : null;
      if (!btn) return;

      var productForm = btn.closest('product-form-component');
      if (!productForm) return;

      productForm._posthogBuyNow = true;
      // Safety-reset if cart:update never fires (e.g. Shopify blocks the add)
      setTimeout(function () { delete productForm._posthogBuyNow; }, 4000);
    }, true);

    // ─── 2. Bundle Pack Selected (Kaching) ────────────────────────────────────
    // Delegate on document; Kaching renders <kaching-bundle> / <kaching-bundle-deals>.
    // We catch clicks on any child that looks like a selectable pack option.
    document.addEventListener('click', function (event) {
      var kaching = event.target && event.target.closest
        ? event.target.closest('kaching-bundle, kaching-bundle-deals')
        : null;
      if (!kaching) return;

      // Kaching typically marks the active option with a selected/active class
      // or uses buttons / labelled inputs. We fire on any clickable child.
      var packEl = event.target.closest(
        '[class*="pack"], [class*="bundle-option"], [class*="option"], ' +
        '[data-pack], [data-quantity], button, label, [role="button"]'
      );
      if (!packEl || packEl === kaching) return;

      var pd       = window.__posthogProductData || {};
      var rawLabel = packEl.textContent ? packEl.textContent.trim().replace(/\s+/g, ' ') : '';
      var packSize = parseInt(packEl.dataset.quantity || packEl.dataset.packSize || '') || null;

      // Try to pull a numeric price from a nearby element
      var priceEl  = packEl.querySelector('[class*="price"]');
      var priceRaw = priceEl ? priceEl.textContent.replace(/[^0-9.]/g, '') : '';
      var bundlePrice = priceRaw ? parseFloat(priceRaw) : null;

      posthog.capture('Bundle Pack Selected', {
        pack_label:   rawLabel,
        pack_size:    packSize,
        bundle_price: bundlePrice,
        product_name: pd.product_name || ''
      });
    });

    // ─── 6. Homepage Product Card Clicked ─────────────────────────────────────
    document.addEventListener('click', function (event) {
      // Only fire on the homepage template
      var main = document.querySelector('[data-template]');
      if (!main || main.dataset.template !== 'index') return;

      var card = event.target && event.target.closest
        ? event.target.closest('product-card')
        : null;
      if (!card) return;

      var productId   = card.dataset.productId || '';
      var link        = card.querySelector('a');
      var productUrl  = link ? link.getAttribute('href') : '';
      var titleEl     = card.querySelector(
        '.product-card__name, .product-card__title, [class*="product-card__name"], h2, h3'
      );
      var productName = titleEl ? titleEl.textContent.trim() : '';

      var allCards = Array.from(document.querySelectorAll('product-card'));
      var position = allCards.indexOf(card) + 1;

      posthog.capture('Homepage Product Card Clicked', {
        product_id:   productId,
        product_name: productName,
        product_url:  productUrl,
        card_position: position,
        collection:   'homepage'
      });
    });

  }); // end waitForPosthog

})();
